import {
  type BookkeepingUpdatedRealtimeEvent,
  GetBookkeepingProgressionInfoRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import type { JobsApi } from '@/features/jobs/api/job-api'
import { BookkeepingProgressionInfo } from './BookkeepingProgressionInfo'
import { mapBookkeepingProgressionInfoToProps } from './bookkeeping-progression-info-mappers'
import DataLoader from 'dataloader'
import { map, share } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'

export class BookkeepingProgressionStore {
  private readonly _bookkeepingProgressionLoader = new DataLoader(
    (ids: readonly Id[]) =>
      Promise.all(
        ids.map(this._fetchBookkeepingProgressionInfoCore.bind(this)),
      ),
  )

  /**
   * The collection.
   */
  bookkeepingProgressions = collection({
    create: () => new BookkeepingProgressionInfo(),
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
  })

  constructor(private readonly _api: JobsApi) {
    this._api.realtime$
      .pipe(
        map((evt) => evt.getBookkeepingUpdated()),
        filterNonNil(),
        share(),
      )
      .subscribe(this._onRemoteEvent.bind(this))
  }

  /**
   * Fetches the bookkeeping progression.
   */
  async fetchBookkeepingProgressionInfo(
    jobId: Id,
    skipCache = false,
    signal?: AbortSignal,
  ) {
    signal?.throwIfAborted()
    if (skipCache) {
      this._clearBookkeepingProgressionCache(jobId)
    }

    return this._bookkeepingProgressionLoader.load(jobId)
  }

  /**
   * Removes a {@link BookkeepingProgressionInfo} from the DataLoader given a job ID.
   */
  private _clearBookkeepingProgressionCache(jobId: Id) {
    this._bookkeepingProgressionLoader.clear(jobId)
  }

  /**
   * Fetches the bookkeeping info for the given job and adds it to the collection if exists.
   */
  private async _fetchBookkeepingProgressionInfoCore(jobId: Id) {
    const request = new GetBookkeepingProgressionInfoRequest().setId(
      Id.assert(jobId),
    )

    const proto = await this._api.bookkeeping
      .getBookkeepingProgressionInfo(request)
      .then((r) => r.getBookkeepingProgressionInfo())

    if (!proto) {
      return null
    }

    const props = mapBookkeepingProgressionInfoToProps(proto)
    return this.bookkeepingProgressions.set(props)
  }

  /**
   * Handles realtime events.
   */
  private _onRemoteEvent(event: BookkeepingUpdatedRealtimeEvent) {
    const proto = event.getProgressionInfo()

    if (!proto) {
      return
    }

    const props = mapBookkeepingProgressionInfoToProps(proto)
    return this.bookkeepingProgressions.set(props)
  }
}
